import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { confirmAlert } from 'react-confirm-alert';
import DatePicker from 'react-datepicker';
import Modal from 'react-modal';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import SimpleReactValidator from 'simple-react-validator';

import services from '@C/CashiersOnDuty/_utils/data';
import {
  CashierList,
  GunsList,
  OnDuty,
  PastDuty,
} from '@C/CashiersOnDuty/RouteComponents/AssignDuty';
import { DashboardFuelPrice } from '@C/Dashboard/Components';
import DropLoaderLarge from '@C/Utils/DropLoaderLarge';
import { ShiftsList } from '@C/Utils/ShiftsList';
import { authHeader } from '@H';
import { authService } from '@S';
import {
  buildDateTimeFromTimeValue,
  getShiftTimingConfig,
  parseLocalDate,
  TIME_INTERVAL_MINUTES,
} from '@stores/_utils/bunkConfig';
import { useGlobalStore } from '@stores/globalStore';

import './styles.scoped.scss';

let c_list;
let g_list;

const getInitialDutyDateTime = (dateValue) => {
  const baseDate = parseLocalDate(dateValue);
  const now = dayjs();

  return baseDate
    .hour(now.hour())
    .minute(now.minute())
    .second(0)
    .millisecond(0)
    .toDate();
};

const AssignDuty = (props) => {
  const history = useHistory();
  const { date: routeDate } = useParams();
  const bunkConfig = useGlobalStore((state) => state.bunkConfig);
  const shiftList = useGlobalStore((state) => state.shiftList);
  const timeLoggingEnabled = Boolean(bunkConfig?.enable_time_cod);
  const [, forceUpdate] = useState();
  const simpleValidator = useRef(
    new SimpleReactValidator({ autoForceUpdate: { forceUpdate: forceUpdate } }),
  );
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const gunsListRef = useRef();
  const [date, setDate] = useState(dayjs(routeDate).format('YYYY-MM-DD'));
  const [selectedDutyDateTime, setSelectedDutyDateTime] = useState(() =>
    getInitialDutyDateTime(routeDate),
  );
  const [state, setState] = useState({
    cashiersList: [],
    staff_name: 'Select cashier',
    staff_id: '',
    gun_list: [],
    step: 2,
    gunsList: [],
    dutyStatus: Math.random().toString(36).substr(2, 5),
    showModal: false,
  });
  const abortControllerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    history.push(`/cashiers-on-duty/assign-duty/${date}`);
    getDSRDates(date);
    getStaffList();
    getGunsList();
  }, [date]);

  useEffect(() => {
    // show modal if in gunlist and if any of the gun has price_updated === false
    if (state.gunsList.some((item) => item.price_updated === false)) {
      setShowModal(true);
    }
  }, [state.gunsList]);

  useEffect(() => {
    if (
      !timeLoggingEnabled ||
      !state.shift_id ||
      state.shift_id === 'Select shift'
    ) {
      return;
    }

    const { start_time } = getShiftTimingConfig(
      bunkConfig,
      state.shift_id,
      shiftList,
    );

    setSelectedDutyDateTime((prev) =>
      buildDateTimeFromTimeValue(prev || routeDate || date, start_time, prev),
    );
  }, [
    bunkConfig,
    date,
    routeDate,
    shiftList,
    state.shift_id,
    timeLoggingEnabled,
  ]);

  const openModal = () => {
    setShowModal(true);
  };
  const closeModal = () => {
    setShowModal(false);
  };

  const closeModalAndReload = () => {
    setShowModal(false);
    window.location.reload();
  };

  const getDSRDates = (date) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    services
      .getDSRDates(dayjs(date).month() + 1, dayjs(date).year(), { signal })
      .then((response) => {
        const tmp = response.data.data;
        const eventsArr = [
          {
            dsr_locked: [],
          },
          {
            dsr_unlocked: [],
          },
        ];
        tmp.forEach((item) => {
          if (item.lock_status) {
            eventsArr[0]['dsr_locked'].push(new Date(item.time_stamp));
          } else {
            eventsArr[1]['dsr_unlocked'].push(new Date(item.time_stamp));
          }
        });
        setState((state) => ({
          ...state,
          highlightedDates: eventsArr,
        }));
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getStaffList = () => {
    services
      .getStaffList(date)
      .then((response) => {
        const tmp = response.data.data;
        c_list = [];
        for (let i = 0; i < tmp.length; i++) {
          c_list.push({
            uuid: tmp[i].uuid,
            label: `${tmp[i].staff_details.first_name} ${tmp[i].staff_details.last_name}`,
            status: tmp[i].is_available,
            duty_date: props.date,
          });
        }
        setState((state) => ({
          ...state,
          cashiersList: c_list,
        }));
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getGunsList = () => {
    services
      .getGunsList(date)
      .then((response) => {
        const tmp = response.data.data;
        g_list = [];
        for (let i = 0; i < tmp.length; i++) {
          g_list.push({
            uuid: tmp[i].uuid,
            label: tmp[i].name,
            opening: tmp[i].readings.opening_reading,
            tank: tmp[i].tank.uuid,
            status:
              tmp[i].is_available === true &&
              tmp[i].tank.product.is_last_updated === true
                ? true
                : false,
            tank_name: tmp[i].tank.name,
            price_updated: tmp[i].tank.product.is_last_updated,
          });
        }
        setState((state) => ({
          ...state,
          gunsList: g_list,
        }));
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleShiftChange = (value) => {
    setState((state) => ({
      ...state,
      shift_id: value.value,
      shift_name: value.label,
    }));
  };

  const handleCashierChange = (id, name) => {
    setState((state) => ({
      ...state,
      staff_id: id,
      staff_name: name,
    }));
  };

  const handleGunsChange = (e) => {
    setState((state) => ({
      ...state,
      gun_list: e,
    }));
  };

  const handleDate = (e) => {
    setSelectedDutyDateTime(e);
    setDate(dayjs(e).format('YYYY-MM-DD'));
  };

  const resetFields = () => {
    setState((state) => ({
      ...state,
      cashierSelected: Math.random().toString(36).substr(2, 5),
      dutyStatus: Math.random().toString(36).substr(2, 5),
      gunStatus: !state.gunStatus,
    }));
  };

  const submitAssignDuty = () => {
    const formData = {
      staff_id: state.staff_id,
      shift_id: state.shift_id,
      gun_list: state.gun_list,
      cashier_duty_time: timeLoggingEnabled
        ? dayjs(selectedDutyDateTime).format('YYYY-MM-DDTHH:mm:ss')
        : date,
    };

    const headers = authHeader(1);
    axios
      .put(`${process.env.API_URL}/bunk/allocate-cashier/`, formData, {
        headers,
      })
      .then(() => {
        toast.success('Duty assigned successfully');
        window.location.reload();
        simpleValidator.current.hideMessages();
      })
      .catch((error) => {
        toast.error(error.response.data.message);
      });
  };

  const confirmAssignDuty = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="react-confirm-alert-body">
          <h1>Confirm duty assignment</h1>
          <p>
            <strong>Cashier:</strong> {state.staff_name}
          </p>
          <p>
            <strong>Shift:</strong> {state.shift_name}
          </p>
          <p>
            <strong>Duty date:</strong>
            {date}{' '}
            {timeLoggingEnabled && (
              <>
                <strong>Time:</strong>
                {dayjs(selectedDutyDateTime).format('hh:mm A')}
              </>
            )}
          </p>

          <p>
            <strong>Guns:</strong>
          </p>
          <ul>
            {state.gun_list.map((gun) => (
              <li key={gun.gun_id}>
                {gun.name} — {gun.opening_reading}
              </li>
            ))}
          </ul>

          <p style={{ color: '#b45309', fontSize: '0.85em' }}>
            Once confirmed, these details can&apos;t be changed.
          </p>

          <div className="react-confirm-alert-button-group">
            <button
              onClick={() => {
                onClose();
                submitAssignDuty();
              }}
            >
              Yes
            </button>
            <button onClick={onClose}>No</button>
          </div>
        </div>
      ),
    });
  };

  const assignDuty = () => {
    if (!simpleValidator.current.allValid()) {
      simpleValidator.current.showMessages();
      forceUpdate(1);
      return;
    }
    if (state.gun_list.some((item) => item.hasError === true)) {
      // scroll to gunsListRef
      // scroll to the first input with class 'has-error' in the gunsListRef
      gunsListRef.current
        .querySelector('.has-error')
        .scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (
      state.gun_list.length < 1 ||
      state.staff_id === '' ||
      state.shift_id === 'Select shift'
    ) {
      toast.error('Please select cashier/shift/guns before assigning duty');

      return;
    }

    confirmAssignDuty();
  };

  const { gun_list, gunsList, cashiersList, staff_name } = state;

  if (authService.currentUserRole === 'Cashier') {
    return <Redirect to="/dashboard" />;
  }

  return (
    <>
      <section className="widget full-width actions-widget">
        <main>
          <div className="input-wrapper">
            <DatePicker
              selected={selectedDutyDateTime}
              dateFormat={
                timeLoggingEnabled ? 'd MMM yyyy h:mm aa' : 'd MMM yyyy'
              }
              onChange={handleDate}
              maxDate={new Date()}
              highlightDates={state.highlightedDates}
              onMonthChange={getDSRDates}
              popperPlacement="bottom-start"
              popperClassName={
                timeLoggingEnabled ? 'cod-time-logging-popper' : undefined
              }
              showTimeSelect={timeLoggingEnabled}
              timeFormat="h:mm aa"
              timeIntervals={TIME_INTERVAL_MINUTES}
              className="custom-datetime"
            />
            <button className="blue-btn" onClick={openModal}>
              Update fuel price
            </button>
          </div>
          <div className="staff-info-container">
            <div className="set-duty-wrapper">
              <div className="input-wrapper">
                {simpleValidator.current.message(
                  'shift_id',
                  state.shift_id,
                  'required',
                )}
                <ShiftsList
                  value={state.shift_id}
                  onChange={handleShiftChange}
                />
              </div>
              <button className="green-btn" onClick={() => assignDuty()}>
                Assign duty
              </button>
            </div>
          </div>

          <div className="list-container">
            <ul>
              {gun_list.map((item, i) => {
                return (
                  <li key={i}>
                    {item.name} ({item.opening_reading})
                  </li>
                );
              })}
            </ul>
          </div>
        </main>
      </section>
      {(() => {
        if (loading) {
          return <DropLoaderLarge />;
        } else {
          return (
            <>
              <PastDuty
                key={date}
                date={date}
                timeLoggingEnabled={timeLoggingEnabled}
              />

              <OnDuty
                key={state.dutyStatus}
                timeLoggingEnabled={timeLoggingEnabled}
              />

              <CashierList
                onChange={handleCashierChange}
                date={date}
                cashiersList={cashiersList}
              />
              <GunsList
                onChange={handleGunsChange}
                gunsList={gunsList}
                key={state.gunStatus}
                cstmRef={gunsListRef}
              />
              <Modal
                isOpen={showModal}
                onRequestClose={closeModal}
                contentLabel="Fuel price modal"
                shouldCloseOnOverlayClick={true}
                className="sub-xl-modal-container"
                overlayClassName="xl-modal-overlay"
                parentSelector={() =>
                  document.querySelector('.page-components')
                }
                style={{
                  content: {
                    width: '50%',
                  },
                }}
              >
                <div className="modal-component">
                  <DashboardFuelPrice
                    date={date}
                    disabled={true}
                    onRequestClose={closeModalAndReload}
                  />
                </div>
              </Modal>
            </>
          );
        }
      })()}
    </>
  );
};

export { AssignDuty };
